import { useState, useEffect, useRef, useCallback } from 'react';

const getWsUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${hostname}/ws`;
};

export function usePortfolioWebSocket(userId) {
  const [balance, setBalance] = useState(null);
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const isUnmountedRef = useRef(false);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const startPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    // 30초마다 ping 전송
    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: 'ping' }));
      }
    }, 30000);
  }, []);

  const stopPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!userId || isUnmountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    if (wsRef.current?.readyState === WebSocket.CONNECTING) return;

    const wsUrl = `${getWsUrl()}/portfolio/${userId}`;
    console.log('WebSocket 연결 시도:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isUnmountedRef.current) {
          ws.close();
          return;
        }
        console.log('WebSocket 연결됨');
        setConnected(true);
        startPingInterval();
      };

      ws.onmessage = (event) => {
        if (isUnmountedRef.current) return;
        
        const msg = JSON.parse(event.data);
        
        if (msg.type === 'balance') {
          setBalance(msg.data);
          setLastUpdate(new Date());
        } else if (msg.type === 'price') {
          const priceData = msg.data;
          setBalance(prev => {
            if (!prev?.holdings || prev.holdings.length === 0) return prev;

            const updatedHoldings = prev.holdings.map(stock => {
              if (stock.stock_code === priceData.stock_code) {
                const newPrice = priceData.current_price;
                const profitLoss = (newPrice - stock.avg_price) * stock.quantity;
                const profitRate = ((newPrice - stock.avg_price) / stock.avg_price) * 100;

                return {
                  ...stock,
                  current_price: newPrice,
                  profit_loss: profitLoss,
                  profit_rate: profitRate
                };
              }
              return stock;
            });

            // 평가손익 합계
            const totalProfitLoss = updatedHoldings.reduce(
              (sum, s) => sum + s.profit_loss, 0
            );

            // 주식평가금액 합계 = 현재가 × 수량
            const totalStockValue = updatedHoldings.reduce(
              (sum, s) => sum + (s.current_price * s.quantity), 0
            );

            // 총자산: API에서 받은 값 유지 (stock_balance가 없으면 재계산하지 않음)
            // 주식평가 변동분만 반영 (prev.stock_balance가 유효할 때만)
            let totalBalance = prev.total_balance;
            if (prev.stock_balance && prev.stock_balance > 0) {
              const stockDiff = totalStockValue - prev.stock_balance;
              totalBalance = prev.total_balance + stockDiff;
            }

            return {
              ...prev,
              holdings: updatedHoldings,
              profit_loss: totalProfitLoss,
              stock_balance: totalStockValue,
              total_balance: totalBalance
            };
          });
          setLastUpdate(new Date());
        } else if (msg.error) {
          console.error('WebSocket 에러:', msg.error);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket 연결 해제');
        setConnected(false);
        wsRef.current = null;
        stopPingInterval();

        if (!isUnmountedRef.current) {
          clearReconnectTimeout();
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, 10000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket 에러:', error);
      };
    } catch (error) {
      console.error('WebSocket 생성 에러:', error);
    }
  }, [userId, clearReconnectTimeout, startPingInterval, stopPingInterval]);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    stopPingInterval();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, [clearReconnectTimeout, stopPingInterval]);

  const refresh = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'refresh' }));
    }
  }, []);

  useEffect(() => {
    isUnmountedRef.current = false;
    
    if (userId) {
      connect();
    }
    
    return () => {
      isUnmountedRef.current = true;
      clearReconnectTimeout();
      stopPingInterval();
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [userId, connect, clearReconnectTimeout, stopPingInterval]);

  return { balance, connected, lastUpdate, refresh };
}
